import { MouseEventHandler } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, InputField, ProgressBar, Icon, Button, Link } from '@ets/design-system';

import formatBytes from 'utils/formatBytes';

import { useSelector } from '_common/hooks';
import { Modals } from '_common/modals/ModalsSliceTypes';

import { Textarea, type TextareaProps } from '_common/lib/components';

import styles from '../CheckInModal.module.scss';

type UploadCleanProps = {
  reset: MouseEventHandler<HTMLSpanElement>;
  name: string;
  editing?: boolean;
  toggleEditingName: MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>;
  onInputChanged: TextareaProps['onChange'];
  comment?: string;
  modal: Modals;
};

const UploadClean = ({
  reset,
  name,
  editing,
  toggleEditingName,
  onInputChanged,
  comment,
  modal,
}: UploadCleanProps) => {
  const intl = useIntl();

  // redux
  const loading = useSelector((state) => state.modals[modal].loading);
  const file = useSelector((state) => state.modals[modal].file);
  const uploadPercentage = useSelector((state) => state.modals[modal].uploadPercentage);

  const { size } = file;

  return (
    <div>
      <div className={styles.clean}>
        <span className={styles.clearButton} onClick={reset}>
          <Icon icon="xmark" size="3" testId="close" />
        </span>
        <Icon icon="file-lines" size="12" testId="document" color="primary" />
        {editing ? (
          <div>
            <Input
              name="name"
              onChange={onInputChanged}
              value={name}
              size="m"
              placeholder=""
              testId="name"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1rem',
              }}
            >
              <Button size="m" variant="primary" onClick={toggleEditingName} testId="save">
                <FormattedMessage id="global.save" />
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex' }}>
              <span className={styles.value}>{name}</span>
              <span className={styles.append}>{`(${formatBytes(size)})`}</span>
            </div>
            {uploadPercentage === 0 && (
              <Link size="m" variant="primary" onClick={toggleEditingName} testId="edit-name">
                <FormattedMessage id="storage.modals.checkIn.click" />
              </Link>
            )}
          </div>
        )}
        {loading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1.5rem',
            }}
          >
            <ProgressBar
              variant="primary-light"
              progress={uploadPercentage}
              size="m"
              testId="check-in-modal-uploading-clean"
            />
          </div>
        )}
      </div>
      <div className={styles.comment}>
        <InputField
          size="l"
          label={intl.formatMessage({
            id: 'storage.modals.checkIn.leaveComment',
          })}
          testId="comment"
        >
          <Textarea
            plainText
            size="m"
            name="comment"
            type="text"
            initialContent={comment}
            placeholder={intl.formatMessage({
              id: 'storage.modals.checkIn.leaveCommentPlaceholder',
            })}
            onChange={onInputChanged}
            testId="leave-a-comment-textarea"
          />
        </InputField>
      </div>
    </div>
  );
};

export default UploadClean;
